import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Plus, Truck, FileEdit, Trash2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated, useSession } from '@/core/auth/SessionStore'
import { useCarreteiroStore } from '@/features/carreteiro/carreteiroStore'
import {
  deleteDraft,
  listDrafts,
  loadDraft,
} from '@/features/carreteiro/draftCarreteiroRepo'
import { descartarFichaOutbox, reabrirFichaRecusada } from '@/features/carreteiro/carreteiroRepo'
import { useCarreteiroRecusadas } from '@/core/db/daos'
import { FichasPropriasNoServidor } from '@/features/carreteiro/FichasPropriasNoServidor'
import { refreshLocaisCargaPorProjeto } from '@/core/sync/catalogSync'
import type { DraftCarreteiroFicha, OutCarreteiro } from '@/core/db/entities'

/**
 * Index do módulo carreteiro:
 *  - "Recusadas pelo servidor": fichas que o SIGA não aceitou (medição
 *    gerada, contrato, viagem inválida…). Não são reenviadas. Tap reabre como
 *    rascunho com o erro destacado; recusas sem correção só podem ser descartadas.
 *  - Fichas em andamento (drafts) + "Nova ficha". O operador pode manter N
 *    fichas em paralelo e alternar entre elas.
 */
export function CarreteiroIndexRoute() {
  const authed = useIsAuthenticated()
  const session = useSession()
  const navigate = useNavigate()
  const store = useCarreteiroStore()
  const recusadas = useCarreteiroRecusadas()
  const [drafts, setDrafts] = useState<DraftCarreteiroFicha[] | null>(null)
  const projetoAtualId = session?.projetoAtualId ?? null

  useEffect(() => {
    let cancelled = false
    listDrafts().then((d) => {
      if (!cancelled) setDrafts(d)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Garante que os locais de carga do projeto atual estão sincronizados.
  // Caso típico: admin seleciona projeto e entra direto no carreteiro — o
  // sync inicial não pegou os locais desse projeto. Catch-up idempotente.
  useEffect(() => {
    if (projetoAtualId == null) return
    refreshLocaisCargaPorProjeto(projetoAtualId).catch((e) =>
      console.warn('[carreteiro/index] sync de locais_carga falhou', e),
    )
  }, [projetoAtualId])

  if (!authed) return <Navigate to="/login" replace />

  async function abrirDraft(d: DraftCarreteiroFicha) {
    const state = await loadDraft(d.id)
    if (!state) {
      // Draft corrompido — remove
      await deleteDraft(d.id)
      setDrafts((all) => (all ? all.filter((x) => x.id !== d.id) : all))
      return
    }
    store.loadFromState(state)
    navigate(state.obraId != null && state.mveId != null ? '/carreteiro/step1' : '/carreteiro/step0')
  }

  function novaFicha() {
    store.reset()
    navigate('/carreteiro/step0')
  }

  async function removerDraft(id: string) {
    if (!confirm('Descartar essa ficha em andamento? As viagens serão perdidas.')) return
    await deleteDraft(id)
    setDrafts((all) => (all ? all.filter((x) => x.id !== id) : all))
    // Se for o draft atual no store, reseta pra evitar continuar editando algo deletado.
    if (store.id === id) store.reset()
  }

  async function abrirRecusada(f: OutCarreteiro) {
    if (!f.rejeicao?.corrigivel) {
      await descartarRecusada(f)
      return
    }
    const state = await reabrirFichaRecusada(f.id)
    if (!state) {
      alert('Não foi possível reabrir esta ficha para correção. Ela só pode ser descartada.')
      return
    }
    store.loadFromState(state)
    navigate('/carreteiro/step1')
  }

  async function descartarRecusada(f: OutCarreteiro) {
    const motivo = f.rejeicao?.message ?? f.failureReason ?? 'recusada pelo servidor'
    if (!confirm(`Descartar esta ficha?\n\n${motivo}\n\nAs viagens serão perdidas.`)) return
    await descartarFichaOutbox(f.id)
  }

  const hasDrafts = drafts != null && drafts.length > 0
  const hasRecusadas = recusadas != null && recusadas.length > 0

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-on-surface">Carreteiro</h1>
            <p className="text-xs text-on-surface-dim">
              {hasDrafts
                ? `${drafts!.length} ficha${drafts!.length > 1 ? 's' : ''} em andamento`
                : 'Fichas em andamento'}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto pb-32">
        <div className="p-4 max-w-2xl mx-auto space-y-3">
          <FichasPropriasNoServidor />
          {hasRecusadas && (
            <section className="space-y-3">
              <h2 className="text-xs font-bold text-bad uppercase px-1">
                Recusadas pelo servidor ({recusadas!.length})
              </h2>
              {recusadas!.map((f) => (
                <RecusadaCard key={f.id} ficha={f} onOpen={abrirRecusada} onDelete={descartarRecusada} />
              ))}
              {(hasDrafts || drafts != null) && (
                <h2 className="text-xs font-bold text-on-surface-dim uppercase px-1 pt-2">Em andamento</h2>
              )}
            </section>
          )}

          {drafts == null ? (
            <p className="text-sm text-on-surface-dim text-center py-8">Carregando…</p>
          ) : hasDrafts ? (
            drafts.map((d) => (
              <DraftCard key={d.id} draft={d} onOpen={abrirDraft} onDelete={removerDraft} />
            ))
          ) : (
            <div className="card-surface-variant px-4 py-8 text-center">
              <Truck className="h-10 w-10 text-on-surface-dim mx-auto" />
              <p className="text-on-surface font-medium mt-3">Nenhuma ficha em andamento</p>
              <p className="text-sm text-on-surface-dim mt-1">
                Toque em <span className="font-bold text-accent">+ Nova ficha</span> pra começar.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button
          variant="primary"
          size="lg"
          onClick={novaFicha}
          className="w-full max-w-2xl mx-auto flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Nova ficha
        </Button>
      </div>
    </main>
  )
}

function RecusadaCard({
  ficha,
  onOpen,
  onDelete,
}: {
  ficha: OutCarreteiro
  onOpen: (f: OutCarreteiro) => void
  onDelete: (f: OutCarreteiro) => void
}) {
  const corrigivel = ficha.rejeicao?.corrigivel ?? false
  return (
    <div className="card-surface border border-bad/60 flex items-stretch overflow-hidden">
      <button type="button" onClick={() => onOpen(ficha)} className="flex-1 p-4 text-left min-w-0">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-bad shrink-0" />
          <p className="text-on-surface font-medium truncate">{ficha.mvePrefixo ?? '(sem caminhão)'}</p>
          <span className="text-[10px] uppercase tracking-wide bg-bad/15 text-bad px-1.5 py-0.5 rounded ml-auto shrink-0">
            {ficha.tipo === 'R' ? 'Rodoviário' : 'Urbana'}
          </span>
        </div>
        <p className="text-xs text-on-surface-dim mt-1 truncate">
          {ficha.obraNome || '(sem obra)'} · {formatBR(ficha.dt)}
        </p>
        <p className="text-sm text-on-surface mt-2">
          {ficha.rejeicao?.message ?? ficha.failureReason ?? 'Recusada pelo servidor'}
        </p>
        <p className={`text-xs mt-1 ${corrigivel ? 'text-accent' : 'text-on-surface-dim'}`}>
          {corrigivel ? 'Toque para corrigir' : 'Sem correção pelo app — toque para descartar'}
        </p>
      </button>
      <button
        type="button"
        onClick={() => onDelete(ficha)}
        className="px-3 text-bad hover:bg-bad/10 transition-colors flex items-center"
        aria-label="Descartar"
      >
        <Trash2 className="h-5 w-5" />
      </button>
    </div>
  )
}

function DraftCard({
  draft,
  onOpen,
  onDelete,
}: {
  draft: DraftCarreteiroFicha
  onOpen: (d: DraftCarreteiroFicha) => void
  onDelete: (id: string) => void
}) {
  const tipoLabel = draft.tipo === 'R' ? 'Rodoviário' : 'Urbana'
  const mveLabel =
    draft.mvePrefixo == null
      ? '(sem caminhão)'
      : draft.mvePlaca
        ? `${draft.mvePrefixo} · ${draft.mvePlaca}`
        : draft.mvePrefixo

  return (
    <div className="card-surface flex items-stretch overflow-hidden">
      <button type="button" onClick={() => onOpen(draft)} className="flex-1 p-4 text-left min-w-0">
        <div className="flex items-center gap-2">
          <FileEdit className="h-4 w-4 text-accent shrink-0" />
          <p className="text-on-surface font-medium truncate">{mveLabel}</p>
          <span className="text-[10px] uppercase tracking-wide bg-accent/15 text-accent px-1.5 py-0.5 rounded ml-auto shrink-0">
            {tipoLabel}
          </span>
        </div>
        <p className="text-xs text-on-surface-dim mt-1 truncate">{draft.obraNome || '(sem obra)'}</p>
        <p className="text-xs text-on-surface-dim mt-1">
          {formatBR(draft.dt)} · {draft.viagensCount} viagem
          {draft.viagensCount === 1 ? '' : 'ns'} ·{' '}
          <span className="text-on-surface-dim">{formatRelative(draft.lastUpdatedAt)}</span>
        </p>
      </button>
      <button
        type="button"
        onClick={() => onDelete(draft.id)}
        className="px-3 text-bad hover:bg-bad/10 transition-colors flex items-center"
        aria-label="Descartar"
      >
        <Trash2 className="h-5 w-5" />
      </button>
    </div>
  )
}

function formatBR(isoDate: string): string {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}

function formatRelative(epochMs: number): string {
  const diff = Date.now() - epochMs
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h}h`
  const d = Math.floor(h / 24)
  return `há ${d}d`
}
